import { useCallback, useEffect, useState } from "react";
import { CheckCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../../lib/api";
import { EMAIL_STATUSES, REPLY_STATUSES, fmtDT, inputCls, BlockedLabel } from "../../lib/outreach";
import { StatusPill } from "./EmailCard";

const Filter = ({ label, value, onChange, options, testid }) => (
  <div className="min-w-[150px]">
    <label className="block text-xs font-medium text-slate-400 mb-1.5">{label}</label>
    <select data-testid={testid} className={inputCls} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="all">All</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

export default function OutreachTable({ isAdmin, onChanged }) {
  const [status, setStatus] = useState("Draft");
  const [step, setStep] = useState("all");
  const [replyStatus, setReplyStatus] = useState("all");
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const params = { status, reply_status: replyStatus };
    if (step !== "all") params.step = step;
    try {
      const { data } = await api.get("/outreach/emails", { params });
      setRows(data);
      setSel([]);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
      setRows([]);
    }
  }, [status, step, replyStatus]);

  useEffect(() => { load(); }, [load]);

  const drafts = (rows || []).filter((r) => r.status === "Draft");
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSel = drafts.length > 0 && drafts.every((d) => sel.includes(d.id));

  const bulkApprove = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/outreach/approve", { ids: sel });
      toast.success(`${data.approved} email(s) scheduled`);
      data.errors.forEach((e) => toast.error(e.error));
      await load();
      onChanged();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="outreach-table">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <Filter label="Status" value={status} onChange={setStatus} options={EMAIL_STATUSES} testid="filter-email-status" />
        <Filter label="Step" value={step} onChange={setStep} options={["1", "2", "3"]} testid="filter-email-step" />
        <Filter label="Reply status" value={replyStatus} onChange={setReplyStatus} options={REPLY_STATUSES} testid="filter-reply-status" />
        {isAdmin && (
          <button data-testid="bulk-approve-button" disabled={!sel.length || busy} onClick={bulkApprove} className="btn-primary rounded-lg px-4 py-2.5 text-sm inline-flex items-center gap-2 ml-auto disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCheck className="w-4 h-4" />} Approve selected ({sel.length})
          </button>
        )}
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#131C31] overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-800">
            <tr>
              <th className="p-3 w-10 text-left">
                {isAdmin && <input data-testid="select-all-drafts" type="checkbox" checked={allSel} onChange={() => setSel(allSel ? [] : drafts.map((d) => d.id))} className="accent-amber-500" />}
              </th>
              <th className="p-3 text-left">Lead</th><th className="p-3 text-left">Step</th><th className="p-3 text-left">Subject</th>
              <th className="p-3 text-left">Status</th><th className="p-3 text-left">Reply</th><th className="p-3 text-left">Scheduled / Sent</th>
            </tr>
          </thead>
          <tbody>
            {rows === null ? (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td data-testid="outreach-table-empty" colSpan={7} className="p-8 text-center text-slate-500">No outreach emails match these filters.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`outreach-row-${r.id}`} className="border-b border-slate-800/60 hover:bg-slate-800/30 transition-colors">
                <td className="p-3">
                  {isAdmin && r.status === "Draft" && <input data-testid={`select-email-${r.id}`} type="checkbox" checked={sel.includes(r.id)} onChange={() => toggle(r.id)} className="accent-amber-500" />}
                </td>
                <td className="p-3">
                  <a href={`/app/leads/${r.lead_id}`} target="_blank" rel="noreferrer" className="font-semibold text-white hover:text-amber-400 transition-colors">{r.company_name}</a>
                  <p className="text-xs text-slate-500">{r.to}</p>
                </td>
                <td className="p-3 text-amber-400 font-bold">{r.step}</td>
                <td className="p-3 text-slate-300 max-w-[260px] truncate">{r.subject}</td>
                <td className="p-3"><div className="flex flex-col items-start gap-1"><StatusPill status={r.status} />{r.status === "Draft" && <BlockedLabel reason={r.blocked_reason} testid={`blocked-${r.id}`} />}</div></td>
                <td className="p-3 text-xs text-slate-300">{r.reply_status}</td>
                <td className="p-3 text-xs text-slate-400">{fmtDT(r.sent_at || r.scheduled_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
